import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { INotExportedOrderState } from './interfaces/notExportedOrder';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { Order } from '@/coreDomainsModule/entities/order';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import { GetNotExportedOrderUseCase } from '../useCases/getNotExportedOrderUseCase';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { simpleCopy } from '@/utils/deepCopy';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { i18n } from '@/i18n';
import { UpdateOutputFlagUseCase } from '../useCases/updateOutputFlagUseCase';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { UpdateOrderMemoUseCase } from '@/orderTableModule/useCases/updateOrderMemoUseCase';
import { DeleteOrderMemoUseCase } from '@/orderTableModule/useCases/deleteOrderMemoUseCase';
import { diContainer } from '@/coreContainer/container';
import { openOkInformationModal } from '@/corePagesModule/components/modal/modalActions';
import { DownloadOrderExcelUseCase } from '@/orderTableModule/useCases/outputOrderExcelUseCase';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { useExportOrderModalStore } from '../components/exportModal/useExportOrderModalStore';
import type { OrderTemplateDetailEntity } from '@/orderTemplateModule/domains/entities/orderTemplate';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';
import { exportFm } from '@/appLogic/fileManager/export/exportFileManager';
import { NOT_EXPORT_ORDER_TABLE_TYPE } from '@/notExportedOrderModule/constants';

export const useNotExportedOrderStore = defineStore('useNotExportedOrder', () => {
  const t = i18n.t;
  const orderRepository = new OrderRepository();
  const getNotExportedOrderUseCase = new GetNotExportedOrderUseCase();
  const updateOutputFlagUseCase = new UpdateOutputFlagUseCase();
  const updateOrderMemoUseCase = new UpdateOrderMemoUseCase(orderRepository);
  const deleteOrderMemoUseCase = new DeleteOrderMemoUseCase(orderRepository);
  const exportOrderModalStore = useExportOrderModalStore();

  const state = reactive<INotExportedOrderState>({
    items: [],
    pagingOptions: initPagingOptions(30),
    sortKey: undefined,
    isAsc: true,
    loadingState: {
      status: {
        type: 'loading',
      },
    },
    tableContentItems: [],
    tableContentProperties: {} as TableContentHeader,
    checkedIds: [],
    exportModalState: false,
    updateMemoState: {
      loading: {
        status: {
          type: 'none',
        },
      },
      workType: 'update',
    },
  });

  // フィルター情報のComposable
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter('notExportedOrderTable');

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.items = [];
    state.sortKey = undefined;
    state.isAsc = true;
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };
    state.pagingOptions = initPagingOptions(30);
    state.tableContentItems = [];
    state.tableContentProperties = {} as TableContentHeader;
    state.checkedIds = [];
    state.exportModalState = false;
    state.updateMemoState = {
      loading: {
        status: {
          type: 'none',
        },
      },
      workType: 'update',
    };
  };

  const checkedArrivalItems = computed(() => {
    return convertTableContentItemsToUnorderedArrival(state.tableContentItems.filter((item) => state.checkedIds.includes(item.displayId)));
  });

  /**
   * 初期化処理
   */
  const init = async () => {
    $reset();
    state.tableContentProperties = fetchTableContentProperties();
    fetchItems();
  };

  /**
   * オフセットをリセットする
   */
  const resetOffset = () => {
    // pagingOptionsがnullableなのでこうしてます
    if (state.pagingOptions) {
      state.pagingOptions.offset = 0;
    }
  };

  /**
   * UIヘッダーキーをバックエンドのカラム名に変換するヘルパー関数
   * @param uiKey - UIヘッダーキー
   * @returns バックエンドのカラム名、または変換不要な場合は元のキー
   */
  const convertUiKeyToBackendKey = (uiKey?: string): string | undefined => {
    switch (uiKey) {
      case 'OrderDate':
        return 'createdOrderDate';
      case 'OrderDesiredTimeOfArrival':
        return 'desiredDeliveryDate';
      case 'OrderNumber':
        return 'orderNumber';
      case 'OrderEstimateTimeOfArrival':
        return 'estimatedTimeOfArrival';
      case 'OrderAmount':
        return 'orderAmount';
      case 'supplierForecastResponsibleUser':
        return 'aggregateBusinessResponsibleUser';
      default:
        return uiKey; // 変換不要な場合はそのまま返す
    }
  };

  /**
   * 発注の取得
   */
  const fetchItems = async () => {
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    await initTableFilter();

    // 永続化された設定を読み込む
    await loadPreferences();
    const savedPreferences = myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE];

    if (savedPreferences) {
      // ソート設定の適用
      if (savedPreferences.sortKey) {
        state.sortKey = savedPreferences.sortKey;
      }
      if (savedPreferences.isAsc !== undefined) {
        state.isAsc = savedPreferences.isAsc;
      } else {
        state.isAsc = true;
      }

      // ページサイズ設定の適用
      if (savedPreferences.pageSize) {
        if (state.pagingOptions) {
          state.pagingOptions.pageSize = savedPreferences.pageSize;
        } else {
          state.pagingOptions = { ...initPagingOptions(savedPreferences.pageSize), pageSize: savedPreferences.pageSize };
        }
      }
    }

    // UIのソート表示を更新
    if (state.sortKey) {
      state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
        const currentIsAsc = state.isAsc ?? true; // ここで boolean 型を保証
        property.sortOption.isAsc = property.key === state.sortKey ? currentIsAsc : true; // ソート対象外は上向き
        return property;
      });
    }

    // バックエンドに渡すソートキーを変換
    const backendSortKey = convertUiKeyToBackendKey(state.sortKey);

    const result = await getNotExportedOrderUseCase.Execute(
      orderRepository,
      state.pagingOptions,
      backendSortKey,
      state.isAsc,
      cachedFilterItems.value
    );

    switch (result.status.type) {
      case 'success':
        state.items = result.status.data.result;
        state.pagingOptions = result.status.data.pagingOptions;
        state.tableContentItems = convertUnorderedArrivalToTableContentItems(state.items);

        if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
          state.loadingState.status.type = 'zeroMatch';
        } else {
          state.loadingState.status.type = 'success';
        }
        break;
      case 'error':
        state.loadingState = {
          status: {
            type: 'error',
            errorMessage: '取得に失敗しました', // TODO: 本来はresult.status.error.message
          },
        };
        break;
    }
  };

  /**
   * 未発注の取引情報をテーブル表示用の情報に変換する
   * @param unorderedArrivals - 未発注の取引情報の配列
   * @returns テーブル表示用の情報
   */
  const convertUnorderedArrivalToTableContentItems = (unorderedArrivals: Order[]): TableContentItem[] => {
    const displayTableContent: TableContentItem[] = [];
    unorderedArrivals.forEach((unorderedArrival) => {
      displayTableContent.push({
        displayId: unorderedArrival.orderId,
        isVirtualChild: false, // TODO: とりあえずね
        ...unorderedArrival,
      });
    });
    return displayTableContent;
  };

  /**
   * テーブル表示用の情報を未発注の取引情報に変換する
   * @param tableContentItems - テーブル表示用の情報
   * @returns 未発注の取引情報
   */
  const convertTableContentItemsToUnorderedArrival = (tableContentItems: TableContentItem[]): Order[] => {
    const unorderedArrivals: Order[] = [];
    tableContentItems.forEach((tableContentItem) => {
      unorderedArrivals.push({
        ...tableContentItem,
      });
    });
    return unorderedArrivals;
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };
    // ページサイズ設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE]) {
      myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE] = {};
    }
    myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE].pageSize = state.pagingOptions.pageSize;
    await savePreferences();
    await fetchItems();
  };

  /**
   * テーブルのヘッダー情報を取得
   */
  const fetchTableContentProperties = (): TableContentHeader => {
    return {
      contentProperties: [
        {
          key: 'choice',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          propertyPath: { paths: ['choice'], propertyType: 'string' },
        },
        // TODO: 2024/06/25 展開を一旦非表示にする
        // {
        //   key: 'expansion',
        //   isVariableWidth: false,
        //   isNarrowable: false,
        //   isEditable: false,
        //   sortOption: { isSortable: false, isAsc: true },
        //   propertyPath: { paths: ['expansion'], propertyType: 'string' },
        // },
        {
          key: 'OrderDate',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['OrderDate'], propertyType: 'IDate' },
        },
        {
          key: 'aggregateBusinessId',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['aggregateBusinessId'], propertyType: 'string' },
        },
        {
          key: 'warehouseNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
        },
        {
          key: 'warehouse',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          key: 'supplierNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
        },
        {
          key: 'supplierName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierName'], propertyType: 'string' },
        },
        {
          key: 'manufacturer',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
        },
        {
          key: 'businessNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessNumber'], propertyType: 'number' },
        },
        {
          key: 'businessName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessName'], propertyType: 'string' },
        },
        {
          key: 'package',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['package'], propertyType: 'number' },
        },
        {
          key: 'unit',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['unit'], propertyType: 'string' },
        },
        {
          key: 'OrderGroupName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: false,
          },
          propertyPath: { paths: ['orderGroupName'], propertyType: 'string' },
        },
        {
          key: 'OrderInvoiceNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: false,
          },
          propertyPath: { paths: ['orderGroupName'], propertyType: 'string' },
        },
        {
          key: 'OrderDestinationName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: false,
          },
          propertyPath: { paths: ['orderGroupName'], propertyType: 'string' },
        },
        {
          key: 'OrderIssuedNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['orderIssuedNumber'], propertyType: 'string' },
        },
        {
          key: 'OrderNumber', // TODO: 「発注番号」は未実装
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: true,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: [], propertyType: 'string' },
        },
        {
          key: 'OrderDesiredTimeOfArrival',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: true,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['desiredDeliveryDate'], propertyType: 'IDate' },
        },
        {
          key: 'OrderEstimateTimeOfArrival',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: true,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['estimatedTimeOfArrival'], propertyType: 'IDate' },
        },
        {
          key: 'OrderAmount',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: true,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['orderAmount'], propertyType: 'number' },
        },
        {
          key: 'OrderRecommendStatus',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: true,
          sortOption: { isSortable: false, isAsc: true },
          propertyPath: { paths: ['orderStatus'], propertyType: 'string' },
        },
        {
          key: 'supplierForecastResponsibleUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: { isSortable: true, isAsc: true },
          propertyPath: { paths: ['aggregateBusinessResponsibleUsers'], propertyType: 'string' },
        },
        {
          key: 'OrderMemo',
          isVariableWidth: true,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          propertyPath: { paths: [], propertyType: 'string' },
        },
        HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
      ],
      options: {
        stableColumnCount: 1,
        resizableHeaderColumn: true,
      },
    };
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   */
  const sortTableContentItems = async (sortKey: TableContentProperty) => {
    // ユーザーの期待する挙動に合わせてisAscを決定
    const newIsAsc = sortKey.key === state.sortKey ? !state.isAsc : false; // 初回クリックで降順

    state.sortKey = sortKey.key;
    state.isAsc = newIsAsc;

    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE]) {
      myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE] = {};
    }
    myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE].sortKey = state.sortKey;
    myPreferences[NOT_EXPORT_ORDER_TABLE_TYPE].isAsc = state.isAsc;
    await savePreferences();

    // UIのソート表示を更新
    state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
      const currentIsAsc = state.isAsc ?? true;
      property.sortOption.isAsc = property.key === state.sortKey ? currentIsAsc : true; // ソート対象外は上向き
      return property;
    });
    resetOffset();
    fetchItems();
  };

  /**
   * フィルターモーダルの適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    // フィルターモーダルの選択情報を更新
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    // オフセットをリセットしてから表示内容を更新
    resetOffset();
    await fetchItems();
  };

  /**
   * 出力モーダルを閉じた時の処理
   * @param file - 選択されたファイル（ファイル選択オプションの場合）
   */
  const onClosedExportModal = async (file?: File) => {
    const selectedFormat = exportOrderModalStore.state.selectedFormat;
    if (selectedFormat === 'custom' && exportOrderModalStore.state.selectedTemplateId === '') {
      toast.error(t('出力書式を選択してください'));
      return false;
    }
    if (selectedFormat === 'file' && !file) {
      toast.error(t('出力ファイルを選択してください'));
      return false;
    }

    const templateId = selectedFormat === 'custom' ? exportOrderModalStore.state.selectedTemplateId : undefined;

    state.exportModalState = false;

    let templateDetail: OrderTemplateDetailEntity | null = null;

    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    if (templateId) {
      const detailResult = await diContainer.getOrderTemplateDetailUseCase.Execute(templateId);
      if (detailResult.status.type !== 'success') {
        await openOkInformationModal(
          'error',
          t('エラー'),
          'OK',
          detailResult.status.error?.message ?? t('テンプレート詳細の取得に失敗しました')
        );
        return false;
      }
      templateDetail = detailResult.status.data;
    }

    // テンプレートエクセルファイルを取得する
    const result = await diContainer.outputOrderUseCase.Execute(state.checkedIds, templateId);
    if (result.status.type === 'error') {
      // その他のエラーの場合の処理
      // エラーモーダルを表示
      state.loadingState = {
        status: {
          type: 'error',
          errorMessage: '出力情報の取得に失敗しました',
        },
      };
      await openOkInformationModal('error', t('エラー'), 'OK', result.status.error.message);
      return false;
    }

    // 取得したデータを元に、受領用内示のエクセルをダウンロードする
    let exportResult: void | Error;
    if (selectedFormat === 'file' && file) {
      exportResult = await exportFm.exportOrderExcel(result.status.data.excelInputInfo, file);
    } else {
      const downloadExcelUseCase = new DownloadOrderExcelUseCase();
      exportResult = await downloadExcelUseCase.Execute(
        result.status.data.excelInputInfo,
        result.status.data.templateOrderExcelData,
        templateDetail
      );
    }
    if (exportResult instanceof Error) {
      state.loadingState = {
        status: {
          type: 'error',
          errorMessage: '発注エクセルの作成に失敗しました',
        },
      };
      toast.error(t(exportResult.message));
      return false;
    }

    // 発注フラグを立てる
    const isSuccessUpdatedOutputFlag = await updateOutputFlag();
    // 成功トースト表示
    if (isSuccessUpdatedOutputFlag) {
      toast.success(t('ダウンロードしました'));
    }
    return isSuccessUpdatedOutputFlag;
  };

  /**
   * 発注フラグを立てる
   * TODO: そもそも発注機能自体をバックエンドに持っていくので、そこで内包されて不要になったら消す
   */
  const updateOutputFlag = async (): Promise<boolean> => {
    const result = await diContainer.updateOutputFlagUseCase.Execute(state.checkedIds);

    switch (result.status.type) {
      case 'success':
        state.loadingState.status.type = 'success';
        toast.success(t('出力に成功しました。'));
        return true;
      case 'error':
        state.loadingState = {
          status: {
            type: 'error',
            errorMessage: '発注状態の更新に失敗しました',
          },
        };
        break;
    }
    return false;
  };

  /**
   * 発注のメモを更新する
   * @param orderId - 発注ID
   * @param memo - メモ
   */
  const updateMemo = async (orderId: string, memo: string) => {
    // ローディング開始
    state.updateMemoState.workType = 'update';
    state.updateMemoState.loading.status.type = 'loading';

    // メモの更新
    const result = await updateOrderMemoUseCase.Execute(orderId, memo);

    switch (result.status.type) {
      case 'success':
        state.updateMemoState.loading.status.type = 'success';
        toast.success(t('メモの更新に成功しました。'));
        // テーブルデータをリロード
        await fetchItems();
        break;
      case 'error':
        state.updateMemoState.loading = {
          status: {
            type: 'error',
            errorMessage: t('メモの更新に失敗しました。'),
          },
        };
        toast.error(t('メモの更新に失敗しました。'));
        break;
    }
  };

  /**
   * 発注のメモを削除する
   * @param orderId - 発注ID
   */
  const deleteMemo = async (orderId: string) => {
    // ローディング開始
    state.updateMemoState.workType = 'delete';
    state.updateMemoState.loading.status.type = 'loading';
    // メモの削除
    const result = await deleteOrderMemoUseCase.Execute(orderId);
    switch (result.status.type) {
      case 'success':
        state.updateMemoState.loading.status.type = 'success';
        toast.success(t('メモの削除に成功しました。'));
        // テーブルデータをリロード
        await fetchItems();
        break;
      case 'error':
        state.updateMemoState.loading = {
          status: {
            type: 'error',
            errorMessage: t('メモの削除に失敗しました。'),
          },
        };
        toast.error(t('メモの削除に失敗しました。'));
        break;
    }
  };

  return {
    state,
    $reset,
    init,
    resetOffset,
    fetchItems,
    onChangePaging,
    convertTableContentItemsToUnorderedArrival,
    fetchTableContentProperties,
    sortTableContentItems,
    onAppliedFilterModal,
    onClosedExportModal,
    updateMemo,
    deleteMemo,
    getFilterItemsValue,
  };
});
